
import React, { useState, useRef, useEffect } from 'react';
import { Send, X, MessageSquare, Loader2, Mail, Phone, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useAIChat } from '@/hooks/useAIChat';
import { useAISettings } from '@/hooks/useAISettings';
import { toast } from 'sonner';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatAgentProps {
  initialMessage?: string;
  className?: string;
}

// Contact form schema
const contactFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z.string().optional(),
  message: z.string().optional()
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export const ChatAgent = ({ initialMessage = "Hi there! I'm here to help you learn about our emergency planning services. Would you like to know about our different plan options?", className }: ChatAgentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [showNetworkDialog, setShowNetworkDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Get global AI settings to check if chat is enabled
  const { settings } = useAISettings();
  const isChatEnabled = settings.enabled;
  
  // Log the current system prompt to help with debugging
  useEffect(() => {
    console.log("Current system prompt:", settings.systemPrompt);
    console.log("Current provider:", settings.provider);
    console.log("Current model:", settings.model);
  }, [settings.systemPrompt, settings.provider, settings.model]);
  
  const { 
    messages, 
    sendMessage,
    retryLastMessage,
    isLoading,
    error,
    networkError,
    providerStatus
  } = useAIChat();

  // Contact form setup
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: ""
    }
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showContactForm]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && isChatEnabled) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isChatEnabled]);

  // Show contact form when chat is disabled or network errors occur
  useEffect(() => {
    if (isOpen && (!isChatEnabled || networkError)) {
      setShowContactForm(true);
    } else {
      setShowContactForm(false);
    }
  }, [isChatEnabled, isOpen, networkError]);
  
  const handleSend = () => {
    if (!input.trim() || !isChatEnabled) return;
    
    console.log("Sending message with current system prompt:", settings.systemPrompt);
    console.log("Current provider:", settings.provider);
    console.log("Current model:", settings.model);
    
    // Send message directly to AI provider
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const onContactSubmit = (data: ContactFormValues) => {
    // In a production environment, this would send data to your CRM or contact database
    console.log("Contact form submitted:", data);
    
    // Save contact information to localStorage for demo purposes
    const contacts = JSON.parse(localStorage.getItem('emergencyPlanContacts') || '[]');
    contacts.push({
      ...data,
      timestamp: new Date().toISOString(),
      source: 'chat-agent',
      userQuery: messages.filter(m => m.isUser).map(m => m.content).join(' | ')
    });
    localStorage.setItem('emergencyPlanContacts', JSON.stringify(contacts));
    
    // Show success message
    toast.success("Thanks for your information! Our team will contact you shortly.");
    setContactSubmitted(true);
    
    // Close network dialog if open
    setShowNetworkDialog(false);
  };
  
  // Custom welcome message when chat is disabled
  const disabledChatMessage = {
    id: "disabled-chat",
    content: "Our team is briefly away from chat. Please leave your contact information below, and we'll get back to you as soon as possible.",
    isUser: false,
    timestamp: new Date()
  };
  
  // Handle retry after network error
  const handleRetry = () => {
    setShowNetworkDialog(false);
    retryLastMessage();
  };

  return (
    <div className={cn("fixed bottom-4 right-4 z-50 flex flex-col", className)}>
      {/* Chat bubble button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90"
        >
          <MessageSquare className="h-6 w-6 text-white" />
        </Button>
      )}

      {/* Chat interface */}
      {isOpen && (
        <div className="flex flex-col bg-card border border-border shadow-xl rounded-lg overflow-hidden w-80 sm:w-96 h-[500px] max-h-[80vh] animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-primary text-primary-foreground p-3 flex items-center justify-between">
            <div className="flex items-center">
              <Avatar className="h-8 w-8 mr-2 bg-primary-foreground/10">
                <MessageSquare className="h-4 w-4 text-primary-foreground" />
              </Avatar>
              <div>
                <h3 className="font-medium">Emergency Plan Assistant</h3>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Messages container */}
          <div className="flex-1 overflow-y-auto p-3 bg-muted/10 space-y-4">
            {isChatEnabled ? (
              // Show regular chat when enabled
              <>
                {messages.map((message) => (
                  <div 
                    key={message.id}
                    className={cn(
                      "flex flex-col max-w-[85%] rounded-lg p-3 text-sm",
                      message.isUser 
                        ? "ml-auto bg-primary text-primary-foreground" 
                        : "mr-auto bg-muted"
                    )}
                  >
                    {message.content}
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex items-center space-x-2 text-muted-foreground text-sm p-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Thinking...</span>
                  </div>
                )}
              </>
            ) : (
              // Show only disabled message when chat is turned off
              <div 
                key={disabledChatMessage.id}
                className="mr-auto bg-muted flex flex-col max-w-[85%] rounded-lg p-3 text-sm"
              >
                {disabledChatMessage.content}
              </div>
            )}
            
            {/* Contact form for network errors or when chat is disabled */}
            {(showContactForm || !isChatEnabled) && !contactSubmitted && (
              <div className="bg-card border border-border p-3 rounded-lg">
                <h4 className="font-medium text-primary mb-2">Leave your contact information</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {isChatEnabled 
                    ? "I'm currently having trouble connecting to our systems. Please leave your contact info and I'll message you as soon as the connection is restored!"
                    : "Please fill out this form and our team will get back to you promptly."}
                </p>
                
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onContactSubmit)} className="space-y-3">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Name</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2 text-muted-foreground" />
                              <Input placeholder="Your name" {...field} className="h-8 text-sm" />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Email</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                              <Input placeholder="your@email.com" {...field} className="h-8 text-sm" />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Phone (optional)</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                              <Input placeholder="Your phone number" {...field} className="h-8 text-sm" />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                    
                    <Button type="submit" className="w-full h-8 text-sm">
                      Submit
                    </Button>
                  </form>
                </Form>
              </div>
            )}
            
            {contactSubmitted && (
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-sm text-green-800">
                Thank you! Our team will reach out to you as soon as possible.
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input area (only show when chat is enabled) */}
          <div className="border-t border-border p-3 bg-background">
            <div className="flex items-center space-x-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isChatEnabled ? "Type your message..." : "Chat is currently disabled"}
                className="flex-1"
                ref={inputRef}
                disabled={isLoading || showContactForm || !isChatEnabled || networkError}
              />
              <Button 
                size="icon" 
                onClick={handleSend}
                disabled={!input.trim() || isLoading || showContactForm || !isChatEnabled || networkError}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAgent;
